import React, { Component, createRef } from "react";
import _ from "lodash";
import { is } from "immutable";
import { connect } from "react-redux";
import { select as d3Select } from "d3-selection";
import { axisBottom, axisLeft } from "d3-axis";
import { max as d3max } from "d3-array";
import { scaleLinear, scaleTime } from "d3-scale";
import { line as d3line } from "d3-shape";
import { timeFormat } from "d3-time-format";
import {
	createSelectorCreator,
	defaultMemoize,
	createSelector,
} from "reselect";
import Item from "./PItem";

const immutableSelector = createSelectorCreator(defaultMemoize, is);

function generateLine100Data(minYear, maxYear) {
	return {
		id: "line-100-data",
		values: _.range(minYear, maxYear + 1).map((year) => {
			return {
				year: new Date(year, 1, 1),
				value: 100,
			};
		}),
	};
}

const getDataFilter = ({ data }) => {
	return data
		.get("data")
		.toList()
		.filterNot(_.isNil)
		.filterNot(_.isEmpty)
		.map((item) => {
			let valuesSplit = [];
			item.values.forEach((line) => {
				const [year, value, probability] = _.split(line, ";");
				const years = _.split(year, "-");
				const diff = Number(years[1]) - Number(years[0]);

				for (let i = Number(years[0]); i <= Number(years[0]) + diff; i++) {
					valuesSplit.push({
						year: new Date(i, 1, 1),
						value: +value,
						probability: +probability,
					});
				}
			});
			const valuesSort = _.sortBy(valuesSplit, ["year"]);

			return {
				...item,
				id: item.resourceId,
				values: valuesSort,
			};
		});
};

const dataSelector = immutableSelector(getDataFilter, _.identity);

const dataItemSelector = (state) => state.map.get("selectedResource");
const resourceSelector = createSelector(dataItemSelector, _.identity);

const margin = { top: 20, right: 30, bottom: 30, left: 40 };

class BarChart extends Component {
	xAxisRef = createRef();
	yAxisRef = createRef();
	innerG = createRef();

	state = {
		lines: [],
		height: 0,
		width: 0,
	};

	xAxis = axisBottom().ticks(5).tickSizeOuter(0).tickFormat(timeFormat("%Y"));
	yAxis = axisLeft();

	static getDerivedStateFromProps(nextProps, prevState) {
		const { data, resource, parentNode } = nextProps;

		if (!data || !parentNode.current) return {};
		const { width } = parentNode.current.getBoundingClientRect();
		const { availHeight } = window.screen;
		const height = availHeight - 300;
		const jsData = data.toJS();
		const minYear = data.map((item) => item.minYear).min();
		const maxYear = data.map((item) => item.maxYear).max();
		const xScale = scaleTime()
			.domain([new Date(minYear, 1, 1), new Date(maxYear, 1, 1)])
			.range([margin.left, width - margin.right]);

		const yScale = scaleLinear()
			.domain([0, d3max(jsData, (d) => d.maxValue)])
			.nice()
			.range([height - margin.bottom, margin.top]);

		const line = d3line()
			.x((d) => xScale(d.year))
			.y((d) => yScale(d.value));

		const line100Data = generateLine100Data(minYear, maxYear);
		const line100Path = line(line100Data.values);
		const line100 = {
			...line100Data,
			line: line100Path,
		};

		const lines = data
			.map((item) => {
				return {
					selected: _.get(resource, "props.resourceId") === item.id,
					line: line(item.values),
					id: item.id,
				};
			})
			.sortBy((item) => !item.selected)
			.toJS();

		return {
			lines,
			line100,
			xScale,
			yScale,
			height,
			width,
		};
	}

	componentDidUpdate() {
		this.renderAxis();
	}

	componentDidMount() {
		const { xScale, yScale } = this.state;
		if (xScale && yScale) {
			this.renderAxis();
		}
	}

	renderAxis = () => {
		this.xAxis.scale(this.state.xScale);
		d3Select(this.xAxisRef.current).call(this.xAxis);
		this.yAxis.scale(this.state.yScale);
		d3Select(this.yAxisRef.current).call(this.yAxis);
	};

	render() {
		const { height, width, lines, line100 } = this.state;
		return (
			<svg width={width} height={height}>
				<g
					fill="none"
					stroke="steelblue"
					strokeWidth="1.5"
					strokeLinejoin="round"
					strokeLinecap="round"
					ref={this.innerG}
				>
					{lines.map((d) => {
						return (
							<Item
								key={`line-${d.id}`}
								id={d.id}
								line={d.line}
								noSelectedColor="#ddd"
								selected={d.selected}
								parentRef={this.innerG}
							/>
						);
					})}
					{line100 && (
						<Item
							id={this.state.line100.id}
							line={this.state.line100.line}
							selected={false}
							noSelectedColor="#ff0000"
						/>
					)}
				</g>
				<text
					transform={
						"translate(" + width / 2 + " ," + (height + margin.top - 20) + ")"
					}
					style={{ textAnchor: "middle" }}
				>
					Año
				</text>
				<g
					ref={this.xAxisRef}
					transform={`translate(0, ${height - margin.bottom})`}
				/>
				<text
					transform="rotate(-90)"
					y={0}
					x={0 - height / 2}
					dy="1em"
					style={{ textAnchor: "middle" }}
				>
					Riesgo relativo o tasa.
				</text>
				<g ref={this.yAxisRef} transform={`translate(${margin.left}, 0)`} />
			</svg>
		);
	}
}

function mapStateToProps(state, props) {
	const year = state.filters.get("year");
	return {
		resource: resourceSelector(state),
		year,
		data: dataSelector(state, { ...props, year }),
	};
}

export default connect(mapStateToProps)(BarChart);



// WEBPACK FOOTER //
// ./src/components/barChart/index.js